import { useChannelTalk } from '@repo/channel-talk';
import { useWindowOpen } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { useDialog } from '@repo/ui';
import { useAtomValue } from 'jotai';
import { useRouter } from 'next/router';

import { PreAccountInfo } from '@/components/tax-refund/lookup/result/apply-possible/PreAccountInfo';
import { SUPPORT_BOT_LINK, 홈택스아이디생성주소 } from '@/lib/constants/link';
import { COMMON_PATHS } from '@/lib/constants/paths';
import { useFromTypeStaticData } from '@/lib/hooks/use-simple-auth-from-type';
import { certTypeAtom, simpleAuthFromTypeAtom } from '@/lib/stores/simple-auth';
import { ellipsisName } from '@/lib/utils/ellipsisName';

interface ShowDialogParamType {
  onClickSecondary?: () => unknown;
  onClickPrimary?: () => unknown;
}

export const useSimpleAuthDialog = () => {
  const router = useRouter();
  const { openWindow } = useWindowOpen();
  const { openChannelTalk } = useChannelTalk();
  const { pageName, sendViewEvent, sendClickEvent } = useUserEventLogger();
  const { showDialog, closeDialog } = useDialog();
  const certType = useAtomValue(certTypeAtom);
  const fromType = useAtomValue(simpleAuthFromTypeAtom);
  const { getExitDialogTitle, getExitPath } = useFromTypeStaticData(fromType);

  const showDialogExitConfirm = (params?: ShowDialogParamType) => {
    sendViewEvent(`${pageName}_close-dialog`, undefined, { isAllowRetry: true });
    showDialog({
      id: 'simple-auth-exit-confirm',
      title: getExitDialogTitle(),
      buttons: {
        secondary: {
          text: '계속하기',
          onClick: () => {
            sendClickEvent(`${pageName}_close-cancel`);
            closeDialog('simple-auth-exit-confirm');
          }
        },
        primary: {
          text: '나가기',
          onClick: () => {
            sendClickEvent(`${pageName}_close-ok`);
            if (params?.onClickPrimary) {
              params.onClickPrimary();
              return;
            }
            router.replace(getExitPath());
          }
        }
      }
    });
  };

  const showDialogNoHometaxUserError = (params?: ShowDialogParamType) => {
    sendViewEvent(`${pageName}_error-nouser`, { type: certType }, { isAllowRetry: true });
    showDialog({
      id: 'no-hometax-user',
      title: '홈택스 회원가입이 필요해요',
      description: (
        <>
          비즈넵 환급은 홈택스 내역을 토대로 환급금을 계산하고 있어요. 홈택스 가입 후 다시 시도해주세요.
          <br />
          <br />
          <b>*개인 주민등록번호로 가입해주세요.</b>
        </>
      ),
      buttons: {
        secondary: {
          text: '다시 조회',
          onClick: () => {
            sendClickEvent(`${pageName}_error-nouser-retry`, { type: certType });
            if (params?.onClickSecondary) {
              params.onClickSecondary();
              return;
            }
            router.back();
          }
        },
        primary: {
          text: '홈택스로 이동',
          onClick: () => {
            sendClickEvent(`${pageName}_error-nouser-hometax`, { type: certType });
            if (params?.onClickPrimary) {
              params.onClickPrimary();
              return;
            }
            openWindow(홈택스아이디생성주소);
          }
        }
      }
    });
  };

  const showDialogNotEqualTinError = (args: { bizName: string } & Omit<ShowDialogParamType, 'onClickSecondary'>) => {
    sendViewEvent(`${pageName}_error-tin`, { type: certType }, { isAllowRetry: true });
    const { bizName, onClickPrimary } = args ?? {};
    showDialog({
      id: 'not-equal-tin',
      title: `${ellipsisName(bizName, '고객')}님의 정보로 인증해주세요`,
      description: '해당 사용자의 정보로만 조회가 가능해요. 입력한 정보를 확인해주세요.',
      buttons: {
        primary: {
          text: '다시 입력하기',
          onClick: () => {
            sendClickEvent(`${pageName}_error-tin-retry`, { type: certType });
            if (onClickPrimary) {
              onClickPrimary();
              return;
            }
            router.back();
          }
        }
      }
    });
  };

  const showDialogAlreadyApplied = ({ loginType, email, onClickSecondary, onClickPrimary }: ShowDialogParamType & { loginType: string; email: string }) => {
    sendViewEvent(`${pageName}_sametin`, undefined, { isAllowRetry: true });
    showDialog({
      id: 'already-applied',
      title: '이미 환급을 신청하셨네요!',
      description: (
        <div className={'flex flex-col gap-y-4 items-start text-left text-[14px] text-gray-700 leading-5 pb-3'}>
          <PreAccountInfo loginType={loginType.toUpperCase()} email={email} />
        </div>
      ),
      buttons: {
        secondary: {
          text: '문의하기',
          onClick: () => {
            sendClickEvent(`${pageName}_sametin-cx`);
            if (onClickSecondary) {
              onClickSecondary();
              return;
            }
            openChannelTalk(SUPPORT_BOT_LINK.조회완료.일반);
          }
        },
        primary: {
          text: '확인',
          onClick: () => {
            sendClickEvent(`${pageName}_sametin-ok`);
            if (onClickPrimary) {
              onClickPrimary();
              return;
            }
            router.replace(COMMON_PATHS.환급조회목록);
          }
        }
      }
    });
  };

  const showDialogAlreadyCompleted = () => {
    sendViewEvent(`${pageName}_sametin-nodata`, undefined, { isAllowRetry: true });
    showDialog({
      id: 'already-completed',
      title: '환급 완료한 사용자입니다.',
      description: '다른 계정으로 환급을 완료한 이력이 있어 조회가 불가능합니다.',
      buttons: {
        primary: {
          text: '확인',
          onClick: () => {
            sendClickEvent(`${pageName}_sametin-nodata-ok`);
            closeDialog('already-completed');
            router.push(COMMON_PATHS.환급조회목록);
          }
        }
      }
    });
  };

  const showDialogHometaxSessionError = () => {
    showDialog({
      id: 'hometax-session-error',
      title: '홈택스 간편인증 로그인 지연 안내',
      description: '오후 6시 이후에 이용해주세요.\n홈택스 접속 이용자가 많아, 현재 로그인 인증이 원활하지 않아요.',
      buttons: {
        primary: {
          onClick: () => {
            router.push(COMMON_PATHS.환급조회목록);
          }
        }
      }
    });
  };

  return {
    showDialogExitConfirm,
    showDialogNoHometaxUserError,
    showDialogNotEqualTinError,
    showDialogAlreadyApplied,
    showDialogAlreadyCompleted,
    showDialogHometaxSessionError
  };
};
